import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { embryoKeys } from '../queries/keys';
import { embryoPaths } from '../queries/paths';

// Embriones disponibles (activos sin transferencia activa) con linaje + storage.
// Filtros: source (fresh|frozen), opu (aspiration.session_id), clivage, donor.
// (api-reference/admin-embryos-available.md)
export function useAvailableEmbryos(filters = {}, opts = {}) {
    return useQuery({
        queryKey: embryoKeys.available(filters),
        queryFn:  ({ signal }) => api.get(embryoPaths.available(), { params: filters, signal }).then(r => r.data),
        placeholderData: (prev) => prev,
        ...opts,
    });
}

// Banco de embriones (admin-embryos.md). Inventario por lineage efectivo (OPU y
// externos). Filtros: q (código), storage (fresh|frozen), source (opu|external),
// donor/sire (id), available (excluye transferidos), limit/offset.
export function useEmbryos(filters = {}, opts = {}) {
    return useQuery({
        queryKey: embryoKeys.list(filters),
        queryFn:  ({ signal }) => api.get(embryoPaths.list(), { params: filters, signal }).then(r => r.data),
        placeholderData: (prev) => prev,
        ...opts,
    });
}

// Página por defecto del listado (contrato: limit def 50 / máx 500).
const LIST_LIMIT = 50;

// Listado paginado "cargar más" (offset). Páginas acumuladas en TanStack Query —
// vite/03 [RIGID]. Reemplaza el limit:500 + aviso de truncado del banco.
export function useEmbryosInfinite(filters, limit = LIST_LIMIT) {
    return useInfiniteQuery({
        queryKey: embryoKeys.infinite(filters, limit),
        queryFn:  ({ pageParam, signal }) =>
            api.get(embryoPaths.list(), { params: { ...filters, limit, offset: pageParam }, signal }).then(r => r.data),
        initialPageParam: 0,
        getNextPageParam: (last) => {
            const p = last?.pagination;
            if (!p) return undefined;
            const next = p.offset + p.limit;
            return next < p.total ? next : undefined;
        },
        placeholderData: (prev) => prev,
    });
}

// Embrión ensamblado (forma completa con lineage). 'new' no consulta.
export function useEmbryo(id) {
    return useQuery({
        queryKey: embryoKeys.detail(id),
        queryFn:  ({ signal }) => api.get(embryoPaths.detail(id), { signal }).then(r => r.data),
        enabled:  !!id && id !== 'new',
    });
}

// Alta de embrión EXTERNO. Respuesta: envoltorio { embryo, discarded } (0025).
export function useCreateEmbryo() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(embryoPaths.list(), payload).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: embryoKeys.all }),
    });
}
